import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DataTable = ({ symptomLogs, cognitiveScores }) => {
  const [activeTab, setActiveTab] = useState('symptoms');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data) => {
    return [...data]?.sort((a, b) => {
      if (sortConfig?.direction === 'asc') {
        return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
      }
      return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
    });
  };

  const getCurrentData = () => {
    const data = activeTab === 'symptoms' ? symptomLogs : cognitiveScores;
    const sortedData = sortData(data);
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData?.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = () => {
    const data = activeTab === 'symptoms' ? symptomLogs : cognitiveScores;
    return Math.ceil(data?.length / itemsPerPage);
  };

  const SortIcon = ({ column }) => {
    if (sortConfig?.key !== column) {
      return <Icon name="ArrowUpDown" size={14} color="#9ca3af" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} color="#2563eb" />
      : <Icon name="ArrowDown" size={14} color="#2563eb" />;
  };

  const getSeverityColor = (severity) => {
    if (severity <= 3) return 'text-green-600 bg-green-50';
    if (severity <= 6) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const SymptomTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-4">
              <button 
                onClick={() => handleSort('date')}
                className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <span>Date</span>
                <SortIcon column="date" />
              </button>
            </th>
            <th className="text-left py-3 px-4">
              <button 
                onClick={() => handleSort('symptomType')}
                className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <span>Symptom Type</span>
                <SortIcon column="symptomType" />
              </button>
            </th>
            <th className="text-left py-3 px-4">
              <button 
                onClick={() => handleSort('severity')}
                className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <span>Severity</span>
                <SortIcon column="severity" />
              </button>
            </th>
            <th className="text-left py-3 px-4">Notes</th>
            <th className="text-left py-3 px-4">Time</th>
          </tr>
        </thead>
        <tbody>
          {getCurrentData()?.map((log, index) => (
            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-3 px-4 text-sm text-gray-900">
                {new Date(log.date)?.toLocaleDateString()}
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Activity" size={16} color="#6b7280" />
                  <span className="text-sm text-gray-900">{log?.symptomType}</span>
                </div>
              </td>
              <td className="py-3 px-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(log?.severity)}`}>
                  {log?.severity}/10
                </span>
              </td>
              <td className="py-3 px-4 text-sm text-gray-600 max-w-xs truncate">
                {log?.notes || 'No notes'}
              </td>
              <td className="py-3 px-4 text-sm text-gray-500">
                {new Date(log.date)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const CognitiveTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-4">
              <button 
                onClick={() => handleSort('date')}
                className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <span>Date</span>
                <SortIcon column="date" />
              </button>
            </th>
            <th className="text-left py-3 px-4">
              <button 
                onClick={() => handleSort('gameType')}
                className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <span>Game Type</span>
                <SortIcon column="gameType" />
              </button>
            </th>
            <th className="text-left py-3 px-4">
              <button 
                onClick={() => handleSort('score')}
                className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <span>Score</span>
                <SortIcon column="score" />
              </button>
            </th>
            <th className="text-left py-3 px-4">
              <button 
                onClick={() => handleSort('reactionTime')}
                className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <span>Reaction Time</span>
                <SortIcon column="reactionTime" />
              </button>
            </th>
            <th className="text-left py-3 px-4">Duration</th>
          </tr>
        </thead>
        <tbody>
          {getCurrentData()?.map((score, index) => (
            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-3 px-4 text-sm text-gray-900">
                {new Date(score.date)?.toLocaleDateString()}
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Brain" size={16} color="#6b7280" />
                  <span className="text-sm text-gray-900">{score?.gameType}</span>
                </div>
              </td>
              <td className="py-3 px-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(score?.score)}`}>
                  {score?.score}%
                </span>
              </td>
              <td className="py-3 px-4 text-sm text-gray-900">
                {score?.reactionTime}ms
              </td>
              <td className="py-3 px-4 text-sm text-gray-500">
                {score?.duration}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          <button
            onClick={() => {
              setActiveTab('symptoms');
              setCurrentPage(1);
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'symptoms' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Icon name="Activity" size={16} />
              <span>Symptom Logs ({symptomLogs?.length})</span>
            </div>
          </button>
          <button
            onClick={() => {
              setActiveTab('cognitive');
              setCurrentPage(1);
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'cognitive' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Icon name="Brain" size={16} />
              <span>Cognitive Scores ({cognitiveScores?.length})</span>
            </div>
          </button>
        </nav>
      </div>
      {/* Table Content */}
      <div className="p-6">
        {activeTab === 'symptoms' ? <SymptomTable /> : <CognitiveTable />}
        
        {/* Pagination */}
        {getTotalPages() > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, activeTab === 'symptoms' ? symptomLogs?.length : cognitiveScores?.length)} of {activeTab === 'symptoms' ? symptomLogs?.length : cognitiveScores?.length} entries
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                iconName="ChevronLeft"
              >
                Previous
              </Button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: getTotalPages() }, (_, i) => i + 1)?.map((page) => (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                ))}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages()))}
                disabled={currentPage === getTotalPages()}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataTable;